/**
 * Item Onderdelen Beheer - Beheer koppelingen tussen producten en onderdelen
 * Bijv: Motor → Oliefilter, Impeller, V-snaar
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import voorraadItemOnderdelenService, { VoorraadItemOnderdeel, VoorraadItemOnderdeelCreate } from '../../services/voorraadItemOnderdelen.service';
import apiFetch from '../../services/api';
import '../onderdelen/Onderdelen.css';

interface VoorraadItem {
  id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  artikelnummer: string | null;
  groep_id: number;
}

const ItemOnderdelenBeheer = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [parentItem, setParentItem] = useState<VoorraadItem | null>(null);
  const [onderdelen, setOnderdelen] = useState<VoorraadItemOnderdeel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKoppeling, setEditingKoppeling] = useState<VoorraadItemOnderdeel | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; koppeling: VoorraadItemOnderdeel | null }>({
    show: false,
    koppeling: null
  });

  // Form state
  const [beschikbareOnderdelen, setBeschikbareOnderdelen] = useState<VoorraadItem[]>([]);
  const [formData, setFormData] = useState<{
    child_item_id: number | null;
    onderdeel_type: string;
    vervangingsinterval_uren: number | null;
    vervangingsinterval_maanden: number | null;
    aantal: number;
    verplicht: boolean;
    opmerkingen: string;
  }>({
    child_item_id: null,
    onderdeel_type: '',
    vervangingsinterval_uren: null,
    vervangingsinterval_maanden: null,
    aantal: 1,
    verplicht: false,
    opmerkingen: ''
  });
  const [zoekOnderdeel, setZoekOnderdeel] = useState('');

  useEffect(() => {
    if (itemId) {
      loadData();
    }
  }, [itemId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Laad parent item
      const item = await apiFetch(`/api/v1/voorraad/items/${itemId}`);
      setParentItem(item);

      // Laad gekoppelde onderdelen
      const koppelingen = await voorraadItemOnderdelenService.getOnderdelenVoorItem(parseInt(itemId!));
      setOnderdelen(koppelingen);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van gegevens');
    } finally {
      setLoading(false);
    }
  };

  const loadBeschikbareOnderdelen = async () => {
    try {
      /* Alle producten als mogelijk onderdeel. Stond op '/api/v1/voorraad/items?limit=100',
         maar dat endpoint stelt groep_id verplicht en kent geen limit: het antwoord was 422
         en de keuzelijst bleef altijd leeg. Daardoor kon je nergens een onderdeel koppelen.
         Gemeld door Dylan 23-09-2026. '/items/all' bestaat wel en levert alle producten. */
      const items = await apiFetch('/api/v1/voorraad/items/all');
      // Filter huidige item en al gekoppelde items eruit
      const bestaandeIds = onderdelen.map(o => o.child_item_id);
      const filtered = items.filter((item: VoorraadItem) =>
        item.id !== parseInt(itemId!) && !bestaandeIds.includes(item.id)
      );
      setBeschikbareOnderdelen(filtered);
    } catch (err: any) {
      console.error('Fout bij laden beschikbare onderdelen:', err);
      setError(err.message || 'Kon de productenlijst niet laden');
    }
  };

  const handleAddClick = async () => {
    await loadBeschikbareOnderdelen();
    setFormData({
      child_item_id: null,
      onderdeel_type: '',
      vervangingsinterval_uren: null,
      vervangingsinterval_maanden: null,
      aantal: 1,
      verplicht: false,
      opmerkingen: ''
    });
    setZoekOnderdeel('');
    setShowAddModal(true);
  };

  const handleEditClick = (koppeling: VoorraadItemOnderdeel) => {
    setEditingKoppeling(koppeling);
    setFormData({
      child_item_id: koppeling.child_item_id,
      onderdeel_type: koppeling.onderdeel_type || '',
      vervangingsinterval_uren: koppeling.vervangingsinterval_uren,
      vervangingsinterval_maanden: koppeling.vervangingsinterval_maanden,
      aantal: koppeling.aantal,
      verplicht: koppeling.verplicht,
      opmerkingen: koppeling.opmerkingen || ''
    });
    setShowEditModal(true);
  };

  const handleAdd = async () => {
    if (!formData.child_item_id) {
      setError('Selecteer een onderdeel');
      return;
    }

    try {
      const data: VoorraadItemOnderdeelCreate = {
        parent_item_id: parseInt(itemId!),
        child_item_id: formData.child_item_id,
        onderdeel_type: formData.onderdeel_type || undefined,
        vervangingsinterval_uren: formData.vervangingsinterval_uren || undefined,
        vervangingsinterval_maanden: formData.vervangingsinterval_maanden || undefined,
        aantal: formData.aantal,
        verplicht: formData.verplicht,
        opmerkingen: formData.opmerkingen || undefined
      };

      await voorraadItemOnderdelenService.create(data);
      setShowAddModal(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij toevoegen onderdeel');
    }
  };

  const handleUpdate = async () => {
    if (!editingKoppeling) return;

    try {
      await voorraadItemOnderdelenService.update(editingKoppeling.id, {
        onderdeel_type: formData.onderdeel_type || undefined,
        vervangingsinterval_uren: formData.vervangingsinterval_uren,
        vervangingsinterval_maanden: formData.vervangingsinterval_maanden,
        aantal: formData.aantal,
        verplicht: formData.verplicht,
        opmerkingen: formData.opmerkingen || undefined
      });
      setShowEditModal(false);
      setEditingKoppeling(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij bijwerken onderdeel');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.koppeling) return;

    try {
      await voorraadItemOnderdelenService.delete(deleteModal.koppeling.id);
      setDeleteModal({ show: false, koppeling: null });
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen');
      setDeleteModal({ show: false, koppeling: null });
    }
  };

  const selectOnderdeel = (item: VoorraadItem) => {
    setFormData(prev => ({
      ...prev,
      child_item_id: item.id,
      onderdeel_type: item.onderdeel_type
    }));
    setZoekOnderdeel(`${item.fabrikant || ''} ${item.model || ''} ${item.artikelnummer || ''} - ${item.onderdeel_type}`.trim());
  };

  const getFilteredOnderdelen = () => {
    if (!zoekOnderdeel) return beschikbareOnderdelen.slice(0, 20);
    const zoekLower = zoekOnderdeel.toLowerCase();
    return beschikbareOnderdelen
      .filter(item =>
        (item.fabrikant && item.fabrikant.toLowerCase().includes(zoekLower)) ||
        (item.model && item.model.toLowerCase().includes(zoekLower)) ||
        (item.artikelnummer && item.artikelnummer.toLowerCase().includes(zoekLower)) ||
        item.onderdeel_type.toLowerCase().includes(zoekLower)
      )
      .slice(0, 20);
  };

  if (loading && !parentItem) {
    return <div className="loading">Laden...</div>;
  }

  if (!parentItem) {
    return <div className="error-message">Item niet gevonden</div>;
  }

  return (
    <div className="motor-modellen-list">
      <div className="page-header">
        <div>
          <button
            className="btn-small btn-secondary"
            onClick={() => navigate(-1)}
            style={{ marginBottom: '0.5rem' }}
          >
            &larr; Terug
          </button>
          <h1>Onderdelen voor {parentItem.fabrikant} {parentItem.model}</h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            {parentItem.onderdeel_type} {parentItem.artikelnummer ? `(${parentItem.artikelnummer})` : ''}
          </p>
        </div>
        <button className="btn-lg btn-primary" onClick={handleAddClick}>
          + Onderdeel Koppelen
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="motor-modellen-table">
        <div
          style={{
            display: 'flex',
            backgroundColor: '#f8fafc',
            borderBottom: '2px solid #e2e8f0',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#475569',
            padding: '0.75rem 1rem',
            alignItems: 'center'
          }}
        >
          <div style={{ flex: '1.5' }}>Type</div>
          <div style={{ flex: '1' }}>Fabrikant</div>
          <div style={{ flex: '1' }}>Artikelnr.</div>
          <div style={{ flex: '0.8' }}>Interval (uren)</div>
          <div style={{ flex: '0.8' }}>Interval (mnd)</div>
          <div style={{ flex: '0.5' }}>Aantal</div>
          <div style={{ flex: '0.5' }}>Verplicht</div>
          <div style={{ flex: '1.5' }}>Acties</div>
        </div>

        <div>
          {onderdelen.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
              Geen onderdelen gekoppeld aan dit product
            </div>
          ) : (
            onderdelen.map(koppeling => (
              <div
                key={koppeling.id}
                style={{
                  display: 'flex',
                  padding: '0.75rem 1rem',
                  alignItems: 'center',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: '0.9375rem',
                  backgroundColor: 'white'
                }}
              >
                <div style={{ flex: '1.5' }}>{koppeling.onderdeel_type || '-'}</div>
                <div style={{ flex: '1' }}>{koppeling.child_item?.fabrikant || '-'}</div>
                <div style={{ flex: '1' }}>{koppeling.child_item?.artikelnummer || '-'}</div>
                <div style={{ flex: '0.8' }}>{koppeling.vervangingsinterval_uren || '-'}</div>
                <div style={{ flex: '0.8' }}>{koppeling.vervangingsinterval_maanden || '-'}</div>
                <div style={{ flex: '0.5' }}>{koppeling.aantal}</div>
                <div style={{ flex: '0.5' }}>
                  {koppeling.verplicht ? (
                    <span style={{ color: '#16a34a' }}>Ja</span>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>Nee</span>
                  )}
                </div>
                <div style={{ flex: '1.5', display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => handleEditClick(koppeling)}
                  >
                    Bewerken
                  </button>
                  <button
                    className="btn-small btn-danger"
                    onClick={() => setDeleteModal({ show: true, koppeling })}
                  >
                    Verwijderen
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="summary-info">
        <p><strong>Totaal:</strong> {onderdelen.length} onderdelen gekoppeld</p>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Onderdeel Koppelen</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Zoek onderdeel *
              </label>
              <input
                type="text"
                value={zoekOnderdeel}
                onChange={(e) => {
                  setZoekOnderdeel(e.target.value);
                  setFormData(prev => ({ ...prev, child_item_id: null }));
                }}
                placeholder="Zoek op fabrikant, artikelnr, type..."
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.9375rem'
                }}
              />
              {zoekOnderdeel && !formData.child_item_id && (
                <div style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto',
                  marginTop: '0.25rem'
                }}>
                  {getFilteredOnderdelen().map(item => (
                    <div
                      key={item.id}
                      onClick={() => selectOnderdeel(item)}
                      style={{
                        padding: '0.5rem',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <strong>{item.onderdeel_type}</strong> - {item.fabrikant || 'Onbekend'} {item.artikelnummer ? `(${item.artikelnummer})` : ''}
                    </div>
                  ))}
                  {getFilteredOnderdelen().length === 0 && (
                    <div style={{ padding: '0.5rem', color: '#94a3b8' }}>
                      Geen onderdelen gevonden
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Interval (draaiuren)
                </label>
                <input
                  type="number"
                  value={formData.vervangingsinterval_uren || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vervangingsinterval_uren: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  placeholder="bijv. 200"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Interval (maanden)
                </label>
                <input
                  type="number"
                  value={formData.vervangingsinterval_maanden || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vervangingsinterval_maanden: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  placeholder="bijv. 12"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Aantal
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.aantal}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    aantal: parseInt(e.target.value) || 1
                  }))}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.verplicht}
                    onChange={(e) => setFormData(prev => ({ ...prev, verplicht: e.target.checked }))}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Verplicht bij onderhoud
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Opmerkingen
              </label>
              <textarea
                value={formData.opmerkingen}
                onChange={(e) => setFormData(prev => ({ ...prev, opmerkingen: e.target.value }))}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.9375rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Annuleren
              </button>
              <button
                onClick={handleAdd}
                disabled={!formData.child_item_id}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: formData.child_item_id ? '#3b82f6' : '#94a3b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: formData.child_item_id ? 'pointer' : 'not-allowed',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Toevoegen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingKoppeling && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Onderdeel Bewerken</h3>

            <div style={{
              background: '#f8f9fa',
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <strong>{editingKoppeling.child_item?.onderdeel_type}</strong><br />
              {editingKoppeling.child_item?.fabrikant} {editingKoppeling.child_item?.artikelnummer ? `(${editingKoppeling.child_item?.artikelnummer})` : ''}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Interval (draaiuren)
                </label>
                <input
                  type="number"
                  value={formData.vervangingsinterval_uren || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vervangingsinterval_uren: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  placeholder="bijv. 200"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Interval (maanden)
                </label>
                <input
                  type="number"
                  value={formData.vervangingsinterval_maanden || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vervangingsinterval_maanden: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  placeholder="bijv. 12"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Aantal
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.aantal}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    aantal: parseInt(e.target.value) || 1
                  }))}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    fontSize: '0.9375rem'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.verplicht}
                    onChange={(e) => setFormData(prev => ({ ...prev, verplicht: e.target.checked }))}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Verplicht bij onderhoud
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Opmerkingen
              </label>
              <textarea
                value={formData.opmerkingen}
                onChange={(e) => setFormData(prev => ({ ...prev, opmerkingen: e.target.value }))}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.9375rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingKoppeling(null);
                }}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Annuleren
              </button>
              <button
                onClick={handleUpdate}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.show && deleteModal.koppeling && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc3545' }}>
              Koppeling Verwijderen
            </h3>
            <p style={{ color: '#666', marginBottom: '0.5rem' }}>
              Weet je zeker dat je deze koppeling wilt verwijderen?
            </p>
            <p style={{
              background: '#f8f9fa',
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <strong>{deleteModal.koppeling.onderdeel_type}</strong><br />
              {deleteModal.koppeling.child_item?.fabrikant} {deleteModal.koppeling.child_item?.artikelnummer}
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteModal({ show: false, koppeling: null })}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Annuleren
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemOnderdelenBeheer;
